import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Typography, Scrollbar } from '@ann/web-components';
import { ChatInput } from './components';
import UCPMessage from './components/UCPMessage';
import { useChatContext } from '../../../../context/ChatContext';
import { useChatCommunication } from '../../../../hooks/useChatCommunication';
import { useStore, useWebSocketScope } from '../../../../../../../hooks';
import { WebSocketScope } from '../../../../../../../utils/enums';
import dayjs from 'dayjs';
import {
  ChatPanelContainer,
  MessagesContainer,
  MessageBubble,
  MessageContainer,
  InputContainer,
  ChatPanelContent,
} from './ChatPanel.styled';

const AGENT_ID = process.env.REACT_APP_UCP_AGENT_UUID;

const ChatPanel = ({ contactId }) => {
  const { authStore } = useStore();
  const chatContext = useChatContext();
  const { sendMessage, isConnected } = useChatCommunication();
  
  const { conversation } = chatContext;
  const { messages, agentIsTyping, conversationID } = conversation;
  
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // Ref to accumulate streaming chunks
  const streamingChunksRef = useRef({
    content: '',
    messageId: null,
  });
  
  // Ref to track expected conversation ID from HTTP response
  // This prevents accepting WebSocket messages from other users' conversations
  const expectedConversationRef = useRef(null);

  // Scroll automático al final cuando hay nuevos mensajes
  useEffect(() => {
    if (bottomRef.current) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }, 100);
    }
  }, [messages]);

  // Clear conversation when contactId changes
  const prevContactIdRef = useRef(contactId);
  useEffect(() => {
    if (prevContactIdRef.current !== contactId) {
      chatContext.clearConversation();
      streamingChunksRef.current = { chunks: {}, messageId: null, totalReceived: 0, isComplete: false };
      expectedConversationRef.current = null;
      prevContactIdRef.current = contactId;
    }
  }, [contactId, chatContext]);

  // Subscribe to WebSocket for agent responses (streaming chunks)
  useWebSocketScope(WebSocketScope.CONVERSATION, useCallback((wsMessage) => {
    const payload = wsMessage.payload || {};
    const event = wsMessage.event;
    
    // Filter by conversation ID - only accept messages for our conversation
    const currentConversationId = conversationID;
    const expectedConvId = expectedConversationRef.current;
    const payloadConvId = payload.conversationUuid;
    
    // CRITICAL: Only accept messages that match our expected/current conversation
    // This prevents cross-user message leakage
    const validConversationId = currentConversationId || expectedConvId;
    
    if (validConversationId) {
      // We have an expected conversation - only accept matching messages
      if (payloadConvId && payloadConvId !== validConversationId) {
        return;
      }
    } else {
      // No expected conversation ID yet - only accept if we're actively waiting
      if (!agentIsTyping) {
        return;
      }
      // ACCEPT first message and lock to this conversation
      if (payloadConvId) {
        expectedConversationRef.current = payloadConvId;
        chatContext.setConversationID(payloadConvId);
      }
    }
    
    // Handle streaming DELTA and COMPLETE events (chunks)
    // DELTA = intermediate chunks, COMPLETE = final chunk
    if ((event === 'DELTA' || event === 'COMPLETE') && payload.chunkContent !== undefined) {
      // Initialize chunks storage for this message if needed
      if (!streamingChunksRef.current.chunks) {
        streamingChunksRef.current = { 
          chunks: {}, 
          messageId: null, 
          totalReceived: 0,
          isComplete: false 
        };
      }
      
      // Store chunk by index (handles out-of-order arrival)
      const chunkIndex = payload.chunkIndex;
      if (!streamingChunksRef.current.chunks[chunkIndex]) {
        streamingChunksRef.current.chunks[chunkIndex] = payload.chunkContent;
        streamingChunksRef.current.totalReceived++;
      }
      streamingChunksRef.current.messageId = payload.messageId;
      
      // Mark complete when we receive the COMPLETE event or isLastChunk
      if (event === 'COMPLETE' || payload.isLastChunk) {
        streamingChunksRef.current.isComplete = true;
        streamingChunksRef.current.lastChunkIndex = chunkIndex;
      }
      
      // Conversation ID is already set in the filter logic above when we lock to the first message
      
      // Check if we have all chunks (complete flag set and all indices received)
      const { chunks, isComplete, lastChunkIndex, totalReceived } = streamingChunksRef.current;
      const expectedCount = lastChunkIndex !== undefined ? lastChunkIndex + 1 : null;
      
      if (isComplete && expectedCount !== null && totalReceived >= expectedCount) {
        // Assemble message in order
        let completeMessage = '';
        for (let i = 0; i <= lastChunkIndex; i++) {
          completeMessage += chunks[i] || '';
        }
        
        const agentResponse = {
          message: completeMessage,
          type: "received",
          time: dayjs().format('HH:mm')
        };
        
        chatContext.setMessages(prev => [...prev, agentResponse]);
        chatContext.setAgentIsTyping(false);
        setIsLoading(false);
        
        // Reset streaming state for next message
        streamingChunksRef.current = { chunks: {}, messageId: null, totalReceived: 0, isComplete: false };
      }
      return;
    }
    
    // Fallback: Handle non-streaming responses (legacy format)
    const responseText = payload.message_text || payload.messageText || payload.message || payload.text || payload.response;
    
    if (responseText) {
      const agentResponse = {
        message: responseText,
        type: "received",
        time: dayjs().format('HH:mm')
      };
      
      chatContext.setMessages(prev => [...prev, agentResponse]);
      chatContext.setAgentIsTyping(false);
      setIsLoading(false);
      
      // Update conversation ID if provided
      const newConversationId = payload.conversation_uuid || payload.conversationUuid;
      if (newConversationId) {
        chatContext.setConversationID(newConversationId);
      }
    }
  }, [chatContext, conversationID, agentIsTyping, contactId]));

  const handleSendMessage = useCallback(async (message, options = {}) => {
    if (!message.trim() || !isConnected) return;

    setIsLoading(true);
    chatContext.setIsSendMessageClicked(true);

    // Agregar mensaje del usuario
    const userMessage = {
      message: message,
      type: "sent",
      time: dayjs().format('HH:mm')
    };

    const localMessages = [...messages, userMessage];
    chatContext.setMessages(localMessages);
    setNewMessage("");

    const params = {
      message_text: message,
      tenant_id: authStore.selectedAccount?.tenantId || "default-tenant",
      sender_uuid: authStore.selectedAccount?.id,
      assistant_uuid: AGENT_ID,
      conversation_uuid: conversationID || "",
      channel: "WEB",
      metadata: JSON.stringify({}),
      params: {
        party_uuid: contactId
      }
    };

    try {
      chatContext.setAgentIsTyping(true);
      
      // If we already have a conversation ID, set it as expected
      if (conversationID) {
        expectedConversationRef.current = conversationID;
      }
      
      const result = await sendMessage(params);
      
      if (result.success) {
        // Store conversation ID from HTTP response if provided
        const newConvId = result.data?.conversation_uuid || result.data?.conversationUuid;
        if (newConvId) {
          expectedConversationRef.current = newConvId;
          chatContext.setConversationID(newConvId);
        }
        // Response will come via WebSocket
      } else {
        chatContext.setAgentIsTyping(false);
        setIsLoading(false);
        console.error('Failed to send message:', result.error);
        
        // Show error message
        const errorMessage = {
          message: "Failed to send message. Please try again.",
          type: "received",
          time: dayjs().format('HH:mm')
        };
        chatContext.setMessages([...localMessages, errorMessage]);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      chatContext.setAgentIsTyping(false);
      setIsLoading(false);
      
      // Agregar mensaje de error
      const errorMessage = {
        message: "Oops! Something went wrong. Please try again.",
        type: "received",
        time: dayjs().format('HH:mm')
      };
      chatContext.setMessages([...localMessages, errorMessage]);
    } finally {
      chatContext.setIsSendMessageClicked(false);
    }
  }, [messages, conversationID, isConnected, sendMessage, chatContext, authStore, contactId]);

  const handleMessageChange = useCallback((e) => {
    setNewMessage(e.target.value);
  }, []);

  const handleSend = useCallback((message) => {
    handleSendMessage(message);
  }, [handleSendMessage]);

  const handleCleanThread = useCallback(() => {
    chatContext.clearConversation();
    setNewMessage('');
  }, [chatContext]);

  return (
    <ChatPanelContainer>
      <ChatPanelContent>
      <Scrollbar size="small">
        <MessagesContainer ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <MessageContainer>
              <MessageBubble $type="system">
                <Typography category="body" weight="regular" size="medium" color="body">
                  Hi there! How can I help you today?
                </Typography>
              </MessageBubble>
            </MessageContainer>
          ) : (
            messages.map((message, index) => (
              <UCPMessage
                key={`${message.type}-${index}-${message.time}`}
                messageItem={message}
                isTyping={false}
              />
            ))
          )}
          {agentIsTyping && (
            <UCPMessage
              messageItem={{ message: "Thinking...", type: "received", time: dayjs().format('HH:mm') }}
              isTyping={true}
            />
          )}
          <div ref={bottomRef} />
        </MessagesContainer>
      </Scrollbar>
      </ChatPanelContent>
      <InputContainer>
        <ChatInput
          value={newMessage}
          onChange={handleMessageChange}
          onSend={handleSend}
          placeholder="Type your instructions here..."
          disabled={isLoading || agentIsTyping || !isConnected}
        />
      </InputContainer>
    </ChatPanelContainer>
  );
};

export default ChatPanel;

